import { type ReactNode, Suspense, useEffect, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { KeepAliveContext } from './KeepAlive';

export interface KeepAliveRouteConfig {
  path: string;
  prefix?: boolean;
  component: React.LazyExoticComponent<React.ComponentType<unknown>>;
  deriveProps?: (pathname: string) => Record<string, unknown>;
}

interface KeepAliveOutletProps {
  routes: KeepAliveRouteConfig[];
  fallback?: ReactNode;
  children: ReactNode;
}

function matchRoute(pathname: string, route: KeepAliveRouteConfig): boolean {
  if (route.prefix) {
    return pathname === route.path || pathname.startsWith(route.path + '/');
  }
  return pathname === route.path;
}

export function KeepAliveOutlet({ routes, fallback, children }: KeepAliveOutletProps) {
  const { pathname } = useLocation();
  const mountedRef = useRef(new Set<string>());
  const prevActiveRef = useRef<string | null>(null);

  const activeRoute = routes.find((r) => matchRoute(pathname, r)) ?? null;
  const activeKey = activeRoute?.path ?? null;

  // Mount route on first visit — mutate ref synchronously so the current render sees it
  if (activeKey && !mountedRef.current.has(activeKey)) {
    mountedRef.current.add(activeKey);
  }

  // Dispatch resize on re-activation (for CodeMirror/react-reflex)
  useEffect(() => {
    if (activeKey && prevActiveRef.current !== activeKey) {
      requestAnimationFrame(() => {
        window.dispatchEvent(new Event('resize'));
      });
    }
    prevActiveRef.current = activeKey;
  }, [activeKey]);

  const isNormalRouteActive = activeKey === null;

  const activeCtx = useMemo(() => ({ isActive: true }), []);
  const inactiveCtx = useMemo(() => ({ isActive: false }), []);

  return (
    <>
      {/* Normal (non-keep-alive) routes */}
      <div style={{ display: isNormalRouteActive ? 'contents' : 'none' }}>{children}</div>

      {/* Keep-alive routes */}
      {routes.map((route) => {
        if (!mountedRef.current.has(route.path)) return null;

        const isActive = activeKey === route.path;
        const Component = route.component;
        const derivedProps = route.deriveProps ? route.deriveProps(pathname) : {};

        return (
          <div
            key={route.path}
            style={{ display: isActive ? 'contents' : 'none' }}
            {...(!isActive ? { inert: true, 'aria-hidden': true as unknown as boolean } : {})}
          >
            <KeepAliveContext.Provider value={isActive ? activeCtx : inactiveCtx}>
              <Suspense fallback={fallback ?? null}>
                <Component {...derivedProps} />
              </Suspense>
            </KeepAliveContext.Provider>
          </div>
        );
      })}
    </>
  );
}
